import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { createCheckoutSession, MEMBERSHIP_TIERS } from '../services/paymentService';
import DiscountCodeInput from '../components/membership/DiscountCodeInput';

const MembershipSelection = () => {
  const { currentUser, userProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [appliedDiscount, setAppliedDiscount] = useState(null);

  // Check if this is a new user (just signed up)
  const isNewUser = userProfile && !userProfile.membershipTier;

  // APN Membership tiers
  const membershipTiers = [
    {
      id: 'tier1',
      name: 'Tier 1',
      price: 75,
      description: 'Analysts & Associates with less than three years of experience (also referred to as recent undergraduates).',
      stripeKey: 'BASIC'
    },
    {
      id: 'tier2', 
      name: 'Tier 2',
      price: 125,
      description: 'Analysts & Associates with more than three years of experience.',
      stripeKey: 'PROFESSIONAL'
    },
    {
      id: 'tier3',
      name: 'Tier 3', 
      price: 175,
      description: 'Managers, VP\'s, Directors, and Managing Directors.',
      stripeKey: 'PREMIUM'
    },
    {
      id: 'tier4',
      name: 'Tier 4',
      price: 225, 
      description: 'C-suite Executives, Principals, Partners, and Business Owners.',
      stripeKey: 'EXECUTIVE'
    },
    {
      id: 'tierUSV',
      name: 'Tier USV',
      price: 75,
      description: 'US Veterans, regardless of seniority level.',
      stripeKey: 'VETERAN'
    }
  ];

  const handleSelectTier = async (tier) => {
    if (!currentUser) {
      alert('Please log in to purchase a membership.');
      return;
    }

    // Prevent multiple simultaneous selections
    if (loading) {
      console.log('Payment already in progress, ignoring duplicate request');
      return;
    }

    setLoading(true);
    setSelectedTier(tier.id);

    try {
      console.log('Processing membership selection:', tier);

      // Create Stripe checkout session for membership
      const response = await fetch('https://us-central1-the-apn.cloudfunctions.net/createCheckoutSession', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: currentUser.uid,
          priceId: `price_${tier.stripeKey.toLowerCase()}_annual`,
          tierKey: tier.stripeKey,
          discountCode: appliedDiscount?.code || null,
          successUrl: `${window.location.origin}/membership/success`,
          cancelUrl: `${window.location.origin}/membership`
        }),
      });

      const session = await response.json();

      if (session.url) {
        // Clear loading state before redirect
        setLoading(false);
        setSelectedTier(null);

        console.log('MembershipSelection: About to redirect to Stripe checkout');
        console.log('Session URL:', session.url);
        console.log('Expected success URL:', `${window.location.origin}/membership/success`);

        // Redirect to Stripe checkout
        window.location.href = session.url;
      } else {
        throw new Error('Failed to create checkout session');
      }
    } catch (error) {
      console.error('Error processing membership:', error);
      alert('Payment failed. Please try again.');
      setLoading(false);
      setSelectedTier(null);
    }
  };

  return (
    <div className="bg-white">
      {/* Page Header */}
      <div className="bg-apn-blue text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <img
                src={`${process.env.PUBLIC_URL}/apn-logos/APN-Tag-white.jpg`}
                alt="APN Logo"
                className="h-16"
              />
            </div>
            <h1 className="text-3xl font-bold">Choose Your Membership</h1>
            <p className="mt-2 text-xl">Select the tier that best fits your career level</p>
          </div>
        </div>
      </div>

      {/* Membership Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Welcome Message for New Users */}
          {isNewUser && (
            <div className="bg-apn-blue text-white rounded-lg p-6 mb-8">
              <div className="text-center">
                <h2 className="text-2xl font-bold mb-2">Welcome to APN, {userProfile?.firstName || 'New Member'}! 🎉</h2>
                <p className="text-lg opacity-90">
                  Thank you for creating your account. Now let's choose your membership tier to unlock all the exclusive benefits of the Accelerated Professionals Network.
                </p>
              </div>
            </div>
          )}

          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {isNewUser ? 'Choose Your Membership Tier' : 'Accelerate your Career with APN'}
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Becoming a member of APN will grant you access to member forums, groups, job boards, project inquiry boards, member directories, and more.
            </p>
          </div>

          <div className="mb-12">
            <p className="text-lg text-gray-600 mb-4">
              Membership with APN is on a "Tier" system. Your Tier will not prevent you from attending or accessing APN events. Please make sure you are signing up for the correct tier as registration is monitored, evaluated, and approved by APN staff.
            </p>
          </div>

          {/* Discount Code Section - Only show after tier selection and during account creation */}
          {selectedTier && (
            <div className="max-w-md mx-auto mb-12">
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Have a Discount Code?</h3>
                <DiscountCodeInput
                  onDiscountApplied={setAppliedDiscount}
                  onDiscountRemoved={() => setAppliedDiscount(null)}
                  originalPrice={selectedTier ? membershipTiers.find(t => t.id === selectedTier)?.price || 0 : 0}
                  disabled={loading}
                />
              </div>
            </div>
          )}

          {/* Membership Tiers */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {membershipTiers.map((tier) => (
              <div key={tier.id} className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200 hover:shadow-xl transition-shadow">
                <div className="bg-apn-blue text-white p-6">
                  <h3 className="text-2xl font-bold">{tier.name}</h3>
                  {appliedDiscount && appliedDiscount.discountAmount === 100 ? (
                    <div>
                      <p className="text-lg line-through opacity-75">${tier.price}</p>
                      <p className="text-3xl font-bold text-green-300">FREE</p>
                      <p className="text-sm text-green-200">with {appliedDiscount.code}</p>
                    </div>
                  ) : appliedDiscount ? (
                    <div>
                      <p className="text-lg line-through opacity-75">${tier.price}</p>
                      <p className="text-3xl font-bold">${(tier.price * (1 - appliedDiscount.discountAmount / 100)).toFixed(2)}</p>
                      <p className="text-sm text-green-200">with {appliedDiscount.code}</p>
                    </div>
                  ) : (
                    <p className="text-3xl font-bold mt-2">${tier.price}</p>
                  )}
                  <p className="text-sm opacity-90">per year</p>
                </div>
                <div className="p-6">
                  <p className="text-gray-600 mb-6">{tier.description}</p>
                  
                  {/* Payment Options */}
                  <div className="space-y-3">
                    <button
                      onClick={() => handleSelectTier(tier)}
                      disabled={loading && selectedTier === tier.id}
                      className="w-full bg-apn-blue text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                      {loading && selectedTier === tier.id ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Processing...
                        </>
                      ) : (
                        <>
                          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                          </svg>
                          Pay with Stripe
                        </>
                      )}
                    </button>
                    
                    <button
                      onClick={() => alert('PayPal integration coming soon!')}
                      className="w-full bg-yellow-500 text-white py-3 px-4 rounded-lg hover:bg-yellow-600 transition duration-200 flex items-center justify-center"
                    >
                      <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.26-.93 4.778-4.005 7.201-9.138 7.201h-2.19a.9.9 0 0 0-.89.756l-1.195 7.583a.384.384 0 0 0 .38.443h3.508c.457 0 .843-.334.917-.788l.038-.207.732-4.64.047-.257a.9.9 0 0 1 .917-.788h.577c3.719 0 6.624-1.51 7.47-5.881.354-1.832.172-3.357-.648-4.588a4.077 4.077 0 0 0-.916-.653z"/>
                      </svg>
                      Pay with PayPal
                    </button>
                  </div>
                  
                  <p className="text-xs text-gray-500 mt-4 text-center">
                    Secure payment processing • Cancel anytime
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Features Section */}
          <div className="mt-16 bg-gray-50 rounded-lg p-8">
            <h3 className="text-2xl font-bold text-center mb-8">All Memberships Include:</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-apn-blue rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h4 className="font-semibold">Member Directory</h4>
                <p className="text-sm text-gray-600">Connect with professionals</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-apn-blue rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H8a2 2 0 01-2-2V8a2 2 0 012-2V6" />
                  </svg>
                </div>
                <h4 className="font-semibold">Job Board</h4>
                <p className="text-sm text-gray-600">Exclusive job opportunities</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-apn-blue rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </div>
                <h4 className="font-semibold">Project Board</h4>
                <p className="text-sm text-gray-600">Find consulting opportunities</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-apn-blue rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
                <h4 className="font-semibold">Forums & Groups</h4>
                <p className="text-sm text-gray-600">Industry discussions</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MembershipSelection;
